import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalFonts,
  signalRadii,
  signalShadows,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  Mono,
  ProgressTrack,
  ScreenFrame,
  SignalButton,
  StatusPill,
  Waveform,
} from '@/common/components/signal';
import { stems } from '../fixtures';

/** Small square S / M toggle beside each stem. */
const StemToggle: React.FC<{ label: string }> = ({ label }) => (
  <Box
    component="span"
    role="button"
    aria-label={label === 'S' ? 'Solo' : 'Mute'}
    sx={{
      width: 24,
      height: 24,
      flex: 'none',
      borderRadius: '7px',
      border: `1px solid ${signalBorders.strong}`,
      color: signalText.secondary,
      fontFamily: signalFonts.mono,
      fontWeight: 600,
      fontSize: '9px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      '&:hover': {
        color: signalText.primary,
        borderColor: 'rgba(255,255,255,0.3)',
      },
    }}
  >
    {label}
  </Box>
);

/** Transport mode chip — 1×, LOOP, GRID. */
const TransportChip: React.FC<{ label: string; active?: boolean }> = ({
  label,
  active = false,
}) => (
  <Box
    component="span"
    sx={{
      padding: '6px 10px',
      borderRadius: `${signalRadii.sm}px`,
      background: active ? signalSurfaces.fillActive : 'transparent',
      border: `1px solid ${active ? signalBorders.strong : signalBorders.default}`,
      color: active ? signalText.primary : signalText.muted,
      fontFamily: signalFonts.mono,
      fontWeight: 600,
      fontSize: '10px',
      cursor: 'pointer',
      '&:hover': active ? undefined : { color: signalText.primary },
    }}
  >
    {label}
  </Box>
);

/**
 * 09 — Stems pack detail.
 *
 * A pack with no DAW behind it, so there is no session to open — the file *is*
 * the deliverable. That makes the per-stem row the whole interface: name, solo,
 * mute, and a waveform wide enough to actually locate a moment in. Muted stems
 * drop to 34% opacity rather than being hidden, so the pack's full shape stays
 * visible while you audition part of it.
 */
export const Screen09Stems: React.FC = () => (
  <ScreenFrame
    index="09"
    title="Stems pack detail"
    note="NO DAW · PER-STEM SOLO / MUTE"
  >
    <Box
      sx={{
        padding: '24px 26px',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        borderBottom: `1px solid ${signalBorders.divider}`,
      }}
    >
      {/* No DAW logo to show — a cyan note glyph stands in, matching the
          STEM PACK pill so the two read as the same statement. */}
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 52,
          height: 52,
          flex: 'none',
          borderRadius: '12px',
          background: signalStatus.stems.bg,
          border: `1px solid ${signalStatus.stems.border}`,
          color: signalColors.cyan,
          fontSize: '22px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ♪
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Box
            component="h3"
            sx={{
              margin: 0,
              fontFamily: signalFonts.display,
              fontWeight: 800,
              fontSize: '30px',
              letterSpacing: '-0.03em',
              lineHeight: 1,
            }}
          >
            Coastline — stems
          </Box>
          <StatusPill status="stems" size={10}>
            STEM PACK
          </StatusPill>
          <StatusPill size={10}>v3</StatusPill>
        </Box>
        <Mono size={11} sx={{ display: 'block', marginTop: '7px' }}>
          14 STEMS · 704 MB · 48 kHz / 24-BIT · 03:58.20 · 112 BPM
        </Mono>
      </Box>

      <SignalButton variant="secondary">Download all</SignalButton>
      <SignalButton glow={false}>Share pack</SignalButton>
    </Box>

    <Box
      sx={{
        padding: '18px 26px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: '18px',
        borderBottom: `1px solid ${signalBorders.divider}`,
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label="Pause"
        sx={{
          width: 40,
          height: 40,
          flex: 'none',
          borderRadius: '999px',
          border: 0,
          cursor: 'pointer',
          background: signalColors.accent,
          color: signalColors.onAccent,
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: signalShadows.accentLg,
          '&:hover': { background: signalColors.accentHover },
        }}
      >
        ❚❚
      </Box>

      <Mono size={15} weight={600} color={signalText.primary}>
        01:42.06
        <Box
          component="span"
          sx={{ color: signalText.faint, fontSize: '12px' }}
        >
          {' / 03:58.20'}
        </Box>
      </Mono>

      <Box sx={{ flex: 1, position: 'relative' }}>
        <ProgressTrack value={43} track={signalSurfaces.trackSoft} />
        <Box
          sx={{
            position: 'absolute',
            left: '43%',
            top: '-4px',
            transform: 'translateX(-50%)',
            width: 13,
            height: 13,
            borderRadius: '999px',
            background: signalText.primary,
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '8px' }}>
        <TransportChip label="1×" active />
        <TransportChip label="LOOP" />
        <TransportChip label="GRID" />
      </Box>
    </Box>

    <Box sx={{ padding: '10px 26px 26px' }}>
      {stems.map((stem) => (
        <Box
          key={stem.name}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '11px 12px',
            borderRadius: `${signalRadii.lg}px`,
            background: stem.rowBg,
            opacity: stem.dim,
            '&:hover': { background: 'rgba(255,255,255,0.045)' },
          }}
        >
          <Box
            sx={{
              width: 210,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
            }}
          >
            <Box
              component="span"
              sx={{ color: signalColors.cyan, fontSize: '11px' }}
            >
              ♪
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {stem.name}
              </Box>
              <Mono size={10} sx={{ display: 'block', marginTop: '2px' }}>
                {stem.meta}
              </Mono>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '5px', flex: 'none' }}>
            <StemToggle label="S" />
            <StemToggle label="M" />
          </Box>

          <Waveform
            bars={stem.wave}
            height={34}
            sx={{ flex: 1, minWidth: 0 }}
          />

          {/* Fixed-width slot so every waveform ends on the same edge whether
              or not the stem carries a badge. */}
          <Box
            component="span"
            sx={{
              width: 74,
              flex: 'none',
              textAlign: 'center',
              fontFamily: signalFonts.mono,
              fontWeight: 600,
              fontSize: '9.5px',
              color: stem.badgeFg,
              background: stem.badgeBg,
              borderRadius: '999px',
              padding: '3px 0',
            }}
          >
            {stem.badge}
          </Box>

          <Box
            component="span"
            sx={{ color: signalText.faint, fontSize: '13px', flex: 'none' }}
          >
            ⋮
          </Box>
        </Box>
      ))}
    </Box>
  </ScreenFrame>
);

export default Screen09Stems;
